/**
 * Collection Requests API service
 *
 * Provides functions for managing Velociraptor collection workflow.
 */

import { apiClient } from './api'
import type {
  CollectionRequest,
  CreateCollectionRequestInput,
  UpdateCollectionRequestInput,
  UpdatePackageInfoInput,
  MarkSentInput,
  MarkIngestedInput,
  OverrideRequestStatusInput,
  StallRequestInput,
  StatusCountResponse,
  ListCollectionRequestsParams,
  DocumentResponse,
  AddTargetsInput,
  AddTargetsResult,
  RemoveTargetsInput,
  RemoveTargetsResult,
} from '../types/collection-requests'

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Build query string from params object
 */
function buildQueryString(params: object): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value))
    }
  }
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

// =============================================================================
// COLLECTION REQUEST API
// =============================================================================

/**
 * List collection requests for a case
 */
export async function listCollectionRequests(
  caseId: string,
  params?: ListCollectionRequestsParams
): Promise<CollectionRequest[]> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<CollectionRequest[]>(`/cases/${caseId}/collection-requests${query}`)
}

/**
 * Get status counts for collection requests in a case, split by dimension
 * (package-build lifecycle and evidence-return progress -- PRP §4).
 */
export async function getStatusCounts(caseId: string): Promise<StatusCountResponse> {
  return apiClient.get<StatusCountResponse>(
    `/cases/${caseId}/collection-requests/status-counts`
  )
}

/**
 * Get a single collection request
 */
export async function getCollectionRequest(
  caseId: string,
  requestId: string
): Promise<CollectionRequest> {
  return apiClient.get<CollectionRequest>(`/cases/${caseId}/collection-requests/${requestId}`)
}

/**
 * Create a new collection request
 */
export async function createCollectionRequest(
  caseId: string,
  request: CreateCollectionRequestInput
): Promise<CollectionRequest> {
  return apiClient.post<CollectionRequest>(`/cases/${caseId}/collection-requests`, request)
}

/**
 * Update a collection request
 */
export async function updateCollectionRequest(
  caseId: string,
  requestId: string,
  input: UpdateCollectionRequestInput
): Promise<CollectionRequest> {
  return apiClient.patch<CollectionRequest>(
    `/cases/${caseId}/collection-requests/${requestId}`,
    input
  )
}

/**
 * Start package generation for a collection request
 */
export async function startGeneration(
  caseId: string,
  requestId: string
): Promise<CollectionRequest> {
  return apiClient.post<CollectionRequest>(
    `/cases/${caseId}/collection-requests/${requestId}/generate`
  )
}

/**
 * Update package info after generation
 */
export async function updatePackageInfo(
  caseId: string,
  requestId: string,
  info: UpdatePackageInfoInput
): Promise<CollectionRequest> {
  return apiClient.post<CollectionRequest>(
    `/cases/${caseId}/collection-requests/${requestId}/package`,
    info
  )
}

/**
 * Mark a collection request as sent
 */
export async function markSent(
  caseId: string,
  requestId: string,
  info: MarkSentInput
): Promise<CollectionRequest> {
  return apiClient.post<CollectionRequest>(
    `/cases/${caseId}/collection-requests/${requestId}/send`,
    info
  )
}

/**
 * Mark a collection request as ingested
 */
export async function markIngested(
  caseId: string,
  requestId: string,
  info: MarkIngestedInput
): Promise<CollectionRequest> {
  return apiClient.post<CollectionRequest>(
    `/cases/${caseId}/collection-requests/${requestId}/ingest`,
    info
  )
}

/**
 * Manually override a collection request's status (audited correction).
 */
export async function overrideRequestStatus(
  caseId: string,
  requestId: string,
  input: OverrideRequestStatusInput
): Promise<CollectionRequest> {
  return apiClient.patch<CollectionRequest>(
    `/cases/${caseId}/collection-requests/${requestId}/status`,
    input
  )
}

/**
 * Mark a collection request as stuck with the client (`sent` -> `stalled`).
 */
export async function stallRequest(
  caseId: string,
  requestId: string,
  input: StallRequestInput
): Promise<CollectionRequest> {
  return apiClient.post<CollectionRequest>(
    `/cases/${caseId}/collection-requests/${requestId}/stall`,
    input
  )
}

/**
 * Clear a stall (`stalled` -> `sent`).
 */
export async function unstallRequest(
  caseId: string,
  requestId: string
): Promise<CollectionRequest> {
  return apiClient.post<CollectionRequest>(
    `/cases/${caseId}/collection-requests/${requestId}/unstall`
  )
}

/**
 * Cancel a collection request
 */
export async function cancelRequest(caseId: string, requestId: string): Promise<CollectionRequest> {
  return apiClient.post<CollectionRequest>(
    `/cases/${caseId}/collection-requests/${requestId}/cancel`
  )
}

/**
 * Delete a collection request
 */
export async function deleteCollectionRequest(caseId: string, requestId: string): Promise<void> {
  await apiClient.delete(`/cases/${caseId}/collection-requests/${requestId}`)
}

// =============================================================================
// TARGET MANAGEMENT API
// =============================================================================

/**
 * Add targets to a collection request
 */
export async function addTargets(
  caseId: string,
  requestId: string,
  input: AddTargetsInput
): Promise<AddTargetsResult> {
  return apiClient.post<AddTargetsResult>(
    `/cases/${caseId}/collection-requests/${requestId}/targets`,
    input
  )
}

/**
 * Remove targets from a collection request
 */
export async function removeTargets(
  caseId: string,
  requestId: string,
  input: RemoveTargetsInput
): Promise<RemoveTargetsResult> {
  return apiClient.delete<RemoveTargetsResult>(
    `/cases/${caseId}/collection-requests/${requestId}/targets`,
    { data: input }
  )
}

// =============================================================================
// DOCUMENT GENERATION API
// =============================================================================

/**
 * Get markdown document for a collection request
 */
export async function getMarkdownDocument(
  caseId: string,
  requestId: string
): Promise<DocumentResponse> {
  return apiClient.get<DocumentResponse>(
    `/cases/${caseId}/collection-requests/${requestId}/document/markdown`
  )
}

/**
 * Get collection form for a collection request
 */
export async function getCollectionForm(
  caseId: string,
  requestId: string
): Promise<DocumentResponse> {
  return apiClient.get<DocumentResponse>(
    `/cases/${caseId}/collection-requests/${requestId}/document/form`
  )
}

/**
 * Download document as file
 */
export function downloadDocument(response: DocumentResponse): void {
  const blob = new Blob([response.content], { type: response.contentType })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = response.filename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

// =============================================================================
// ENDPOINT COLLECTION STATUS API
// =============================================================================

import type { EndpointCollectionStatusesResponse } from '../types/collection-set'

/**
 * Get collection status for all endpoints in a case with active collection requests
 */
export async function getEndpointCollectionStatuses(
  caseId: string
): Promise<EndpointCollectionStatusesResponse> {
  return apiClient.get<EndpointCollectionStatusesResponse>(
    `/cases/${caseId}/endpoints/collection-status`
  )
}
